<script setup lang="ts">
/**
 * 開站時一併設定的**站台機密**欄（批151 C6）——開站精靈第四步與代管核可框共用。
 *
 * - 只有營運主管以上看得到（宿主負責 `v-if`；後端對低於營運主管的人帶值一律 403）。
 * - 欄位清單來自產品代管設定的 `site_secrets` 宣告（宿主優先用 preflight 回的
 *   `site_secret_fields`）。全部**選填**：空白＝這一欄這次不帶，開站後可在站台詳情再設。
 * - `blocker` 非空＝這台主機現在帶不了（代理商主機／agent 不支援／缺加密主鑰）→ 全部停用並說明。
 * - 輸入框 `type=password`、`autocomplete=off`、`spellcheck=false`：不回填、不讓瀏覽器記住
 *   （`new-password` 會讓密碼管理員跳出「產生／儲存密碼」，機密不是使用者的密碼）。值只活在
 *   宿主的 ref 裡，**只在最後送出時**帶進 body（preflight 不帶）；宿主關窗時負責清空。
 */
import { computed } from 'vue'

import { SECRET_MAX_LEN, SECRET_MIN_LEN, type SecretFieldDecl } from '@/lib/siteSecrets'

const props = defineProps<{
  fields: SecretFieldDecl[]
  modelValue: Record<string, string>
  /** 不能填的原因（`null`＝可以填）。 */
  blocker: string | null
}>()
const emit = defineEmits<{ 'update:modelValue': [Record<string, string>] }>()

function set(key: string, v: string) {
  emit('update:modelValue', { ...props.modelValue, [key]: v })
}

function badLength(key: string): boolean {
  const n = (props.modelValue[key] ?? '').trim().length
  return n > 0 && (n < SECRET_MIN_LEN || n > SECRET_MAX_LEN)
}
const filledCount = computed(
  () => Object.values(props.modelValue).filter((v) => typeof v === 'string' && v.trim()).length,
)
</script>

<template>
  <div class="ssi">
    <p class="subtle lead">
      這個產品的代管站需要我方替它設定的機密（例如站台自己要用的第三方服務金鑰）。全部選填；
      留空的欄位可以開站後再到站台詳情設定。值加密保存，之後後台只看得到末四碼。
    </p>
    <p v-if="blocker" class="blocked">{{ blocker }}</p>
    <div class="rows">
      <label v-for="f in fields" :key="f.key" class="row" :class="{ off: !!blocker }">
        <span class="name">
          {{ f.label }}
          <code class="mono env">{{ f.env }}</code>
        </span>
        <input
          type="password"
          autocomplete="off"
          spellcheck="false"
          :maxlength="SECRET_MAX_LEN + 64"
          :disabled="!!blocker"
          :value="modelValue[f.key] ?? ''"
          :placeholder="blocker ? '目前不能設定' : '選填'"
          @input="set(f.key, ($event.target as HTMLInputElement).value)"
        />
        <small v-if="f.hint" class="subtle">{{ f.hint }}</small>
        <small v-if="badLength(f.key)" class="warn-text">長度必須介於 {{ SECRET_MIN_LEN }}～{{ SECRET_MAX_LEN }} 字。</small>
      </label>
    </div>
    <p v-if="!blocker && filledCount" class="subtle lead">將在開站時設定 {{ filledCount }} 項（送出前不會傳到授權中心）。</p>
  </div>
</template>

<style scoped>
.lead {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-12);
}
.blocked {
  margin: 0 0 var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border-strong, var(--border));
  border-radius: var(--r-sm, 6px);
  background: var(--bg);
  color: var(--fg-3);
  font-size: var(--fs-12);
}
.rows {
  display: grid;
  gap: var(--sp-3);
}
.row {
  display: grid;
  gap: 4px;
}
.row.off {
  opacity: 0.6;
}
.name {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.env {
  margin-left: 6px;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.row input {
  width: 100%;
  min-width: 0;
  max-width: 420px;
}
.warn-text {
  color: var(--warning);
}
</style>
